import { createContext, useContext } from 'react'

import type { SessionResponse } from '@aeris/contracts'
import type { Permission } from '@aeris/permissions'

/**
 * The session contract the whole application programs against.
 *
 * Split from the provider so that `auth.tsx` exports only a component: React
 * Fast Refresh cannot refresh a module that mixes components with other
 * exports, and losing state on every edit is a real cost during UI work.
 */

export type AuthState = {
  session: SessionResponse | null
  /** True only while the first `/auth/me` is in flight. */
  isLoading: boolean
  isAuthenticated: boolean
  /**
   * The identity on screen was remembered by this machine, not confirmed by
   * the server — the API is unreachable.
   *
   * Screens use it to say so, and to refuse anything that would need a server
   * to be correct. It is never a permission: what the cache remembers is what
   * to render, and the API judges every request independently when it returns.
   */
  isUnverified: boolean
  /**
   * Whether the interface should offer this action.
   *
   * Never the enforcement. The server checks the same permission independently
   * on every request; this only decides what is worth rendering. Master
   * Specification section 28 is explicit that a frontend check is not a
   * permission.
   */
  allows: (permission: Permission) => boolean
  /**
   * Whether this screen appears for this person.
   *
   * Separate from `allows` because two screens can require the same
   * permission — Vendas and Orçamentos both require `sales.read` — and taking
   * one away cannot revoke the permission the other one needs. Also never the
   * enforcement: it decides what the menu offers, and the API judges every
   * request on its own.
   */
  showsPage: (module: string) => boolean
  /**
   * A conta tem esta chave ligada? (F169)
   *
   * Terceira pergunta, e diferente das outras duas: `allows` e `showsPage`
   * falam da PESSOA dentro da conta; esta fala da CONTA. Quem liga é o console
   * da plataforma, e o admin da loja não opina.
   *
   * Como as outras, não é a proteção: o servidor já não manda a tela em
   * `pages`, e a rota da vertical recusa por conta própria.
   */
  hasFeature: (feature: string) => boolean
  /**
   * Quem opera a instalação, e não uma loja (F119).
   *
   * Fora do modelo de permissões de propósito: `allows` e `showsPage` falam do
   * que alguém pode DENTRO de uma conta e dependem de uma membership. Este é
   * outro eixo — e, como os outros dois, decide só o que vale renderizar. O
   * servidor recusa `/api/v1/platform/*` por conta própria, com o mesmo campo
   * lido da sessão dele.
   */
  isPlatformAdmin: boolean
  /**
   * A plataforma está dentro desta loja por esta sessão (F169).
   *
   * Nulo em toda sessão normal. Quando presente, a faixa de suporte aparece no
   * topo — e `isPlatformAdmin` está falso, porque o servidor o suprime
   * enquanto durar o empréstimo. As duas coisas andam juntas de propósito: o
   * console some do menu exatamente enquanto a faixa está acesa.
   *
   * Isto é o que o OPERADOR vê. O lojista não é avisado de nada, por decisão
   * do dono registrada no adendo da ADR-0035.
   */
  support: { tenantName: string; expiresAt: string } | null
  signIn: (input: { email: string; password: string }) => Promise<void>
  signOut: () => Promise<void>
  /** Devolve a sessão à conta de origem. Ver `support`. */
  exitSupport: () => Promise<void>
  switchContext: (input: {
    tenantId: string
    companyId: string | null
    branchId: string | null
  }) => Promise<void>
}

export const AuthContext = createContext<AuthState | null>(null)

export const useAuth = (): AuthState => {
  const value = useContext(AuthContext)
  if (value === null) {
    throw new Error('useAuth precisa estar dentro de <AuthProvider>')
  }
  return value
}
